import { useMutation, useQuery } from '@tanstack/react-query';

import { ADMIN_API_URL_SUBPATH } from '@/constants';
import { axiosInstance, FinancialClosureItem, Order, Orders, ORDERS } from '@/core';

export const useOrders = () => {
  const queryConfig = {
    url: `${ADMIN_API_URL_SUBPATH}/${ORDERS}`,
  };

  const useOrdersQuery = ({
    limit,
    page,
    start_date,
    end_date,
  }: {
    limit: number;
    page: number;
    start_date: string | null;
    end_date: string | null;
  }) =>
    useQuery<Orders>({
      queryKey: ['orders', limit, page],
      queryFn: async () => {
        const response = await axiosInstance.get<Orders>(`${queryConfig.url}`, {
          params: { limit, page, start_date, end_date },
        });
        return response.data;
      },
    });

  const useOrdersSearch = (search: string) =>
    useQuery<Orders>({
      queryKey: ['ordersSearch', search],
      queryFn: async () => {
        if (search === '') return [];
        const response = await axiosInstance.get(`${queryConfig.url}/search`, {
          params: { id: search },
        });
        return response.data;
      },
    });

  const useOrdersProductExport = (
    since: Date | undefined,
    until: Date | undefined,
    sortBy?: string,
    sortOrder?: 'asc' | 'desc',
    limit?: number,
    page?: number,
    enabled = true,
  ) =>
    useQuery<{ results: FinancialClosureItem[]; total_results: number }>({
      queryKey: [
        'ordersProductExport',
        since?.toISOString(),
        until?.toISOString(),
        sortBy,
        sortOrder,
        limit,
        page,
      ],
      enabled,
      queryFn: async () => {
        const response = await axiosInstance.get(`${queryConfig.url}/product/export`, {
          params: { since, until, lang: 'cs', sort_by: sortBy, sort_order: sortOrder, limit, page },
        });
        const items: {
          id: number;
          variant_id: string;
          plu_code: string;
          name: string;
          total_sold: number;
          total_revenue_czk: string;
          total_taxed_revenue_czk: string;
          tax_value: string;
          is_takeout: number;
        }[] = response.data.results;
        return {
          results: items.map((item) => ({
            id: item.id,
            variant_id: item.variant_id,
            plu_code: item.plu_code,
            name: item.name,
            total_sold: item.total_sold,
            unit_price_with_tax:
              item.total_sold && item.total_sold !== 0
                ? (Number(item.total_taxed_revenue_czk) / item.total_sold).toFixed(2)
                : String(item.total_sold),
            tax_value: `${Number(item.tax_value)} %`,
            total_revenue_czk: item.total_revenue_czk,
            tax_in_czk: (
              Number(item.total_taxed_revenue_czk) - Number(item.total_revenue_czk)
            ).toFixed(2),
            total_taxed_revenue_czk: item.total_taxed_revenue_czk,
            is_takeout: item.is_takeout === 1,
            tax: '',
          })) as FinancialClosureItem[],
          total_results: response.data.total_results as number,
        };
      },
    });

  const exportOrdersProductXlsxMutation = useMutation({
    mutationKey: ['ordersProductExportXlsx'],
    mutationFn: async ({ since, until }: { since: Date | undefined; until: Date | undefined }) => {
      const response = await axiosInstance.get(`${queryConfig.url}/product/export.xlsx`, {
        params: { since, until, lang: 'cs' },
        responseType: 'blob',
      });
      return response.data as Blob;
    },
  });

  const useGetOrderById = (id: string | undefined) =>
    useQuery<Order>({
      queryKey: ['orders', id || ''],
      enabled: !!id,
      queryFn: async () => {
        const response = await axiosInstance.get<Order>(`${queryConfig.url}/${id}`);
        return response.data;
      },
    });

  const deleteMutation = useMutation({
    mutationKey: ['deleteOrder'],
    mutationFn: (id: number) => axiosInstance.delete(`${queryConfig.url}/${id}`),
  });
  return {
    mutations: {
      deleteMutation,
      exportOrdersProductXlsxMutation,
    },
    queries: {
      useOrdersQuery,
      useOrdersSearch,
      useOrdersProductExport,
      useGetOrderById,
    },
  };
};
